import {useCallback, useEffect, useState} from 'react';
import moment from 'moment';
import {
  activateConfig as activateConfigToApi,
  getConfigs as fetchConfigsFromApi,
  saveAndActivateConfig as saveAndActivateConfigToApi,
  saveConfig as saveConfigToApi,
} from '../services/configSnapshotApi';
import {getEdgeFlags} from '../services/edgeFlagsApi';
import {useAsyncAction} from './useAsyncAction';

/**
 * Encapsulates config snapshot loading, saving, activating, and selection state.
 *
 * @param {string} edgeId
 * @param {(jsonText: string) => void} updateCurrentJsonText — callback to sync JSON text into context.
 */
export const useConfigSnapshots = (edgeId, updateCurrentJsonText) => {
  const {loading, error, run, setError} = useAsyncAction();
  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(0);
  const [sgrcontainer, setSgrcontainer] = useState(false);

  // ─── Internal loaders ──────────────────────────────────────────────────────

  const loadConfigs = useCallback(async () => {
    const result = await fetchConfigsFromApi(edgeId);
    if (!result.ok) {
      setError(result.error?.message || 'Unable to load configurations.');
      return;
    }
    const {configs: confs, activeConfig: activeConfigDate} = result.data;
    setConfigs(confs);
    if (activeConfigDate) {
      setActiveConfig(activeConfigDate);
      const activeIndex = confs.findIndex((c) => moment(c.created.toDate()).isSame(activeConfigDate));
      if (activeIndex !== -1) {
        setSelectedConfig(activeIndex);
        updateCurrentJsonText(confs[activeIndex].value);
      }
    } else if (confs.length > 0) {
      setSelectedConfig(0);
      updateCurrentJsonText(confs[0].value);
    }
  }, [edgeId, updateCurrentJsonText, setError]);

  const init = useCallback(async () => {
    await run(async () => {
      await loadConfigs();
      const flagsResult = await getEdgeFlags(edgeId);
      if (flagsResult.ok) {
        setSgrcontainer(flagsResult.data.sgrcontainer ?? false);
      }
      return {ok: true};
    }, 'Unable to load configurations. You may not have permission.');
  }, [edgeId, loadConfigs, run]);

  // ─── Actions ───────────────────────────────────────────────────────────────

  const save = useCallback(async (jsonText, note) => {
    const result = await run(
      () => saveConfigToApi(edgeId, jsonText, note),
      'Unable to save configuration.',
    );
    if (result.ok) await loadConfigs();
  }, [edgeId, run, loadConfigs]);

  const saveAndActivate = useCallback(async (jsonText, note) => {
    const result = await run(
      () => saveAndActivateConfigToApi(edgeId, jsonText, note),
      'Unable to save and apply configuration.',
    );
    if (result.ok) await init();
  }, [edgeId, run, init]);

  const activate = useCallback(async () => {
    const cfg = configs[selectedConfig];
    if (!cfg) return;
    const result = await run(
      () => activateConfigToApi(edgeId, cfg.value, cfg.created),
      'Unable to activate configuration.',
    );
    if (result.ok) await loadConfigs();
  }, [edgeId, configs, selectedConfig, run, loadConfigs]);

  const selectConfig = useCallback((index) => {
    setSelectedConfig(index);
    if (configs[index]?.value) {
      updateCurrentJsonText(configs[index].value);
    }
  }, [configs, updateCurrentJsonText]);

  // ─── Derived state ─────────────────────────────────────────────────────────

  const hasChanges = useCallback(
    (currentJsonText) => {
      if (!configs[selectedConfig]) return false;
      return currentJsonText !== configs[selectedConfig].value;
    },
    [configs, selectedConfig],
  );

  const isSelectedConfigActive = useCallback(() => {
    if (!activeConfig || !configs[selectedConfig]) return false;
    return moment(configs[selectedConfig].created.toDate()).isSame(activeConfig);
  }, [activeConfig, configs, selectedConfig]);

  const isAnyConfigSelected = useCallback(
    () => Number(selectedConfig) !== -1 && !!configs[selectedConfig],
    [selectedConfig, configs],
  );

  const hasActiveConfig = useCallback(() => {
    return !!activeConfig;
  }, [activeConfig]);

  // ─── Bootstrap ─────────────────────────────────────────────────────────────

  useEffect(() => {
    init();
  }, [edgeId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loading,
    error,
    configs,
    activeConfig,
    selectedConfig,
    sgrcontainer,
    save,
    saveAndActivate,
    activate,
    selectConfig,
    hasChanges,
    isSelectedConfigActive,
    isAnyConfigSelected,
    hasActiveConfig,
  };
};

